import React, { useEffect, useState } from 'react';
import { useFetchOrdersDeliveredAtForReport } from 'hooks/useFetchOrdersDeliveredAtForReport';
import { reqStatus, Region } from 'types/orderTypes';
import { useFetchShiftByRegion } from 'hooks/useFetchShift';
import { BillingCourier } from 'components/OrderListTable/BillingCourier/BillingCourier';
import { Loader } from 'components/Loader/Loader';
import Input from 'components/inputs/Input';
import Dropdown from 'components/dropdown/Dropdown';
import {
  CalendarFetchByPeriods,
  getEndDate,
  getStartDate,
} from 'components/OrderListTable/CalendarFetch/CalendarFetchByPeriods';
import { ReqEnum } from 'const/requestStatus';
import './MyDeliveriesReportComposition.scss';
import { CourierInvoice } from '../../types/reportTypes';
import {
  ShiftCourierJson,
  ShiftStatusType,
  ShiftCourierWithDateJson,
} from '../../const/ShiftConst';
import dayjs from 'dayjs';
import { useFetchCourierById } from '../../hooks/useFetchCouriers';
import { CourierJson, TravelMode } from '../../types/bundleTypes';
import { flatten } from '../../utils/array';
import { Helmet } from 'react-helmet';

export const ShiftInvoiceDataToReplace = {
  courierCompanyName: 'unknown',
  courierCompanyNumber: 0,
  courierTravelMode: TravelMode.NONE,
  courierAvgRatingForDateRange: 0,
};

export const errorCourierInvoiceObjReplacment: CourierInvoice = {
  counts: [],
  courierCompanyName: ShiftInvoiceDataToReplace.courierCompanyName,
  courierCompanyNumber: ShiftInvoiceDataToReplace.courierCompanyNumber,
  courierFirstName: '',
  courierId: 0,
  courierLastName: '',
  courierTravelMode: ShiftInvoiceDataToReplace.courierTravelMode,
  courierAvgRatingForDateRange:
    ShiftInvoiceDataToReplace.courierAvgRatingForDateRange,
};

export function findUniqueCourierNameListFromCourierNameList(
  courierNameArray: {
    courierId: number;
    fullName: string;
  }[],
  searchText: string
): any {
  if (courierNameArray) {
    const filteredObject = courierNameArray
      .filter(obj => obj.fullName !== null)
      .filter(obj => obj.fullName !== 'null')
      .filter(obj =>
        obj.fullName.toLowerCase().includes(searchText.toLowerCase())
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName));

    return filteredObject;
  } else {
    return [
      {
        courierId: 0,
        fullName: 'loading...',
      },
    ];
  }
}

function BillingCourierById({
  fetchStatus,
  showInvoiceBySelectedCourier,
  selectedCourierId,
  startDate,
  endDate,
  courierListBookedFetched,
  fetchStatusShift,
  region,
}: {
  fetchStatus: reqStatus;
  showInvoiceBySelectedCourier: CourierInvoice;
  selectedCourierId: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  courierListBookedFetched: ShiftCourierWithDateJson[];
  fetchStatusShift: reqStatus;
  region: Region;
}) {
  return (
    <>
      {fetchStatus === ReqEnum.PENDING && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Loader type="spinner" size="large" />
        </div>
      )}

      {selectedCourierId === null ? (
        <div>
          {' '}
          This courier dont have a courier Id. Talk to the programmers or add it
          yourself
        </div>
      ) : (
        <BillingCourier
          courierInvoiceList={showInvoiceBySelectedCourier}
          fetchStatus={fetchStatus}
          startDate={startDate}
          endDate={endDate}
          courierListBookedFetched={courierListBookedFetched}
          fetchStatusShift={fetchStatusShift}
          region={region}
        />
      )}
    </>
  );
}

export function makeInvoiceBySelectedCourier(
  courierInvoiceList: CourierInvoice[],
  courierNameArray,
  courierName: string
): CourierInvoice {
  if (courierName.length > 0 && courierNameArray && courierNameArray.length) {
    const showInvoiceBySelectedCourier = courierInvoiceList.filter(
      obj => `${obj.courierFirstName} ${obj.courierLastName}` === courierName
    );

    const courierIdForChosenName: string = courierNameArray.filter(
      obj => obj.fullName === courierName
    )[0].courierId;

    if (showInvoiceBySelectedCourier.length === 0) {
      const courierInvoiceObjReplacment: CourierInvoice = {
        counts: [],
        courierCompanyName: ShiftInvoiceDataToReplace.courierCompanyName,
        courierCompanyNumber: ShiftInvoiceDataToReplace.courierCompanyNumber,
        courierFirstName: courierName,
        courierId: Number(courierIdForChosenName),
        courierLastName: '',
        courierTravelMode: ShiftInvoiceDataToReplace.courierTravelMode,
        courierAvgRatingForDateRange:
          ShiftInvoiceDataToReplace.courierAvgRatingForDateRange,
      };

      return courierInvoiceObjReplacment;
    }

    return showInvoiceBySelectedCourier[0];
  } else {
    return errorCourierInvoiceObjReplacment;
  }
}

export function isCourierNameInCourierNameList(
  courierNameArray,
  selectedCourierName: string
): boolean {
  const arrayMatchingSelectedCourierName = courierNameArray
    .map(item => item.fullName)
    .filter(obj => obj.indexOf(selectedCourierName) === 0);

  return arrayMatchingSelectedCourierName.length ? true : false;
}

function useShowInvoiceBySelectedCourier(
  courierInvoiceList: CourierInvoice[],
  selectedCourierName: string,
  fetchStatus: reqStatus,
  searchText: string,
  courierListBookedFetched: ShiftCourierWithDateJson[],
  fetchStatusShift: reqStatus,
  setSelectedCourierName
) {
  const [selectedCourierId, setSelectedCourierId] = useState<number>(0);

  const [showInvoiceBySelectedCourier, setShowInvoiceBySelectedCourier] =
    useState<CourierInvoice | undefined>();

  const [uniqueCourierNameListObject, setUniqueCourierNameListObject] =
    useState<[]>();

  useEffect(() => {
    const updateData = (
      courierInvoiceList,
      selectedCourierName,
      fetchStatus,
      searchText,
      courierListBookedFetched,
      fetchStatusShift,
      setSelectedCourierName
    ) => {
      if (
        fetchStatus === ReqEnum.SUCCESS &&
        fetchStatusShift === ReqEnum.SUCCESS
      ) {
        const courierNameArrayByOrders: {
          courierId: number;
          fullName: string;
        }[] = courierInvoiceList.map(item => {
          const fullname = `${item.courierFirstName} ${item.courierLastName}`;

          return { courierId: item.courierId, fullName: fullname };
        });
        // need to add list by shiftnames. also.

        const courierNameArrayByShifts: {
          courierId: number;
          fullName: string;
        }[] = flatten<any>(
          courierListBookedFetched.map(
            (obj1: ShiftCourierWithDateJson) => obj1.courierDayBooked
          )
        )
          .filter(
            (obj: ShiftCourierJson) => obj.status === ShiftStatusType.BOOKED
          )
          .map((item: ShiftCourierJson) => {
            return { courierId: item.courierId, fullName: item.name };
          });

        const courierNameArrayWithDuplicates = [
          ...courierNameArrayByOrders,
          ...courierNameArrayByShifts,
        ];

        const courierNameArray: {
          courierId: any;
          fullName: string;
        }[] = courierNameArrayWithDuplicates
          .map(obj => obj.courierId)
          .reduce(
            (unique, item) =>
              unique.includes(item) ? unique : [...unique, item],
            []
          )
          .map(item => {
            const newObj = {
              courierId: item,
              fullName: courierNameArrayWithDuplicates.filter(
                obj => obj.courierId === item
              )[0].fullName,
            };
            return newObj;
          });

        setUniqueCourierNameListObject(
          findUniqueCourierNameListFromCourierNameList(
            courierNameArray,
            searchText
          )
        );
        if (
          isCourierNameInCourierNameList(courierNameArray, selectedCourierName)
        ) {
          const showInvoiceBySelectedCourier = makeInvoiceBySelectedCourier(
            courierInvoiceList,
            courierNameArray,
            selectedCourierName
          );

          setShowInvoiceBySelectedCourier(showInvoiceBySelectedCourier);
          if (showInvoiceBySelectedCourier.courierId) {
            setSelectedCourierId(showInvoiceBySelectedCourier.courierId);
          } else {
            setSelectedCourierId(null);
          }
        } else {
          setSelectedCourierId(0);
          setSelectedCourierName('');
        }
      }
    };
    updateData(
      courierInvoiceList,
      selectedCourierName,
      fetchStatus,
      searchText,
      courierListBookedFetched,
      fetchStatusShift,
      setSelectedCourierName
    );
  }, [
    courierInvoiceList,
    courierListBookedFetched,
    fetchStatus,
    fetchStatusShift,
    searchText,
    selectedCourierName,
    setSelectedCourierName,
  ]);

  return [
    selectedCourierId,
    showInvoiceBySelectedCourier,
    uniqueCourierNameListObject,
  ];
}

export function replaceDataOfCourierInvoiceIfMissingCompanyData(
  showInvoiceBySelectedCourier: CourierInvoice,
  fetchCoruierDataStatus: reqStatus,
  courierData: CourierJson
): CourierInvoice {
  if (fetchCoruierDataStatus === ReqEnum.SUCCESS) {
    const courierInvoiceObjReplacment: CourierInvoice = {
      counts: (showInvoiceBySelectedCourier as CourierInvoice).counts,
      courierCompanyName:
        (showInvoiceBySelectedCourier as CourierInvoice).courierCompanyName ===
        ShiftInvoiceDataToReplace.courierCompanyName
          ? (courierData as CourierJson).companyName
          : (showInvoiceBySelectedCourier as CourierInvoice).courierCompanyName,
      courierCompanyNumber:
        (showInvoiceBySelectedCourier as CourierInvoice)
          .courierCompanyNumber === 0
          ? Number((courierData as CourierJson).companyNumber)
          : (showInvoiceBySelectedCourier as CourierInvoice)
              .courierCompanyNumber,
      courierFirstName: (showInvoiceBySelectedCourier as CourierInvoice)
        .courierFirstName,
      courierId: (showInvoiceBySelectedCourier as CourierInvoice).courierId,
      courierLastName: (showInvoiceBySelectedCourier as CourierInvoice)
        .courierLastName,
      courierTravelMode:
        (showInvoiceBySelectedCourier as CourierInvoice).courierTravelMode ===
        ShiftInvoiceDataToReplace.courierTravelMode
          ? (courierData as CourierJson).travelMode
          : (showInvoiceBySelectedCourier as CourierInvoice).courierTravelMode,

      courierAvgRatingForDateRange: (
        showInvoiceBySelectedCourier as CourierInvoice
      ).courierAvgRatingForDateRange,
    };
    return courierInvoiceObjReplacment;
  }
  return showInvoiceBySelectedCourier;
}

export function InvoiceMultipleCourierReportComposition({
  region,
}: {
  region: Region;
}): JSX.Element {
  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());
  const [searchText, setSearchText] = useState<string>('');

  const [courierInvoiceList, fetchStatus] = useFetchOrdersDeliveredAtForReport(
    startDate,
    endDate,
    region
  );

  const [courierListBookedFetched, fetchStatusShift] = useFetchShiftByRegion(
    startDate,
    endDate,
    region
  );

  const [selectedCourierName, setSelectedCourierName] = useState<string>('');

  const [
    selectedCourierId,
    showInvoiceBySelectedCourier,
    uniqueCourierNameListObject,
  ] = useShowInvoiceBySelectedCourier(
    courierInvoiceList,
    selectedCourierName,
    fetchStatus,
    searchText,
    courierListBookedFetched,
    fetchStatusShift,
    setSelectedCourierName
  );

  const [courierData, fetchCoruierDataStatus] = useFetchCourierById(
    (showInvoiceBySelectedCourier as CourierInvoice) === undefined
      ? undefined
      : (showInvoiceBySelectedCourier as CourierInvoice).courierId.toString()
  );

  const UpdatedShowInvoiceBySelectedCourier: CourierInvoice =
    replaceDataOfCourierInvoiceIfMissingCompanyData(
      showInvoiceBySelectedCourier as CourierInvoice,
      fetchCoruierDataStatus,
      courierData
    );

  const fetchValues = values => {
    const { fetchStartDate, fetchEndDate } = values;
    setStartDate(fetchStartDate);
    setEndDate(fetchEndDate);
  };

  const chooseCourier = courierName => {
    setSelectedCourierName(courierName);
  };

  const listOfNames: string[] = uniqueCourierNameListObject
    ? (uniqueCourierNameListObject as any).length > 0
      ? (uniqueCourierNameListObject as any).map(item => item.fullName)
      : ['Empty for this period']
    : ['Loading ...'];

  return (
    <>
      <Helmet>
        <title>Reports - Invoice Courier</title>
      </Helmet>
      <div className="chooseCourierInvoiceRow">
        <Input
          className="searchInput"
          placeholder="Filter courier list.."
          onChange={event => setSearchText(event.target.value)}
          value={searchText}
          autoFocus={searchText !== ''}
          onRef={input => searchText !== '' && input && input.focus()}
          disabled={fetchStatus !== ReqEnum.SUCCESS}
        />
        <Dropdown
          placeholder={
            selectedCourierName
              ? selectedCourierName
              : 'Show invoice by courier'
          }
          onChange={courierName => chooseCourier(courierName)}
          names={listOfNames}
        />
      </div>

      <CalendarFetchByPeriods
        fetchValues={fetchValues}
        givenStartDate={startDate}
        givenEndDate={endDate}
      />
      {endDate.isBefore(dayjs(new Date('2021-06-01'))) &&
        selectedCourierName.length > 0 && (
          <BillingCourierById
            selectedCourierId={selectedCourierId as number}
            fetchStatus={fetchStatus}
            showInvoiceBySelectedCourier={UpdatedShowInvoiceBySelectedCourier}
            startDate={startDate}
            endDate={endDate}
            courierListBookedFetched={courierListBookedFetched}
            fetchStatusShift={fetchStatusShift}
            region={region}
          />
        )}
    </>
  );
}
